import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Label, Input, ListGroup, ListGroupItem } from "reactstrap";
import TextField from "../../../common/TextField";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { getBuilderByIdService, updateBuilderService, getSupplierCommentsService } from "../../../../Services/CRMAdminServices/CRMBuildersService";
import MobileField from "../../../common/MobileField";
import { H3 } from "../../../../AbstractElements";
import DatePicker from "../../../common/DatePicker";
import SelectDatePicker from "../../../common/SelectDatePicker";
import { buildersData } from "../FormData/CrmFormData";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import { getAllStates, getAllAreas } from "../../../../Services/MMPClientServices/PropertySearchService";
import { fontWeight } from "html2canvas/dist/types/css/property-descriptors/font-weight";

const methodOptions = [

  { value: "BDM email us", label: "BDM email us" },
  { value: "Email BDM", label: "Email BDM" },
  { value: "Manual", label: "Manual" },
  { value: "Portal", label: "Portal" },
];

export default function EditCRMBuilders() {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const location = useLocation();
  const { builderID } = useParams<{ builderID: string }>();
  const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';
  interface FormData {
    [key: string]: any;
    statecheckbox: string[];
  }

  const [formData, setFormData] = useState<FormData>({
    ...buildersData,
    method: "", // Method dropdown blank rahe taaki "Select" placeholder dikhe
    statecheckbox: [], // for multi-select
  });
  const [formErrors, setFormErrors] = useState<any>({});
  const [statesData, setStatesData] = useState<{ value: number; label: string }[]>([]);
  const [areasData, setAreasData] = useState<any>([]);
  const [suburb, setSuburb] = useState<any[]>([]);
  const [allComments, setAllComments] = useState<any>([]);
  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`;

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleValueChange = (name: string, value: string | Date | null) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const { data: builderData, isLoading } = useGetRequestQuery(
    ["getBuilderById", builderID],
    () => getBuilderByIdService(builderID),
    { enabled: !!builderID }
  );

  const { 
    
    data: allStatesData,
    isLoading: allStatesLoading,
    isError: allStatesIsError,
  } = useGetRequestQuery(
    ["getAllStates"],
    getAllStates,
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
      const transformedData = allStatesData.data.map((state: any) => ({
        value: state.id,
        label: state.name
      }));
      setStatesData(transformedData);
    }
  }, [allStatesData, allStatesIsError, allStatesLoading]);

  const {
    data: allAreasData,
    isLoading: allAreasLoading,
    isError: allAreasIsError,
  } = useGetRequestQuery(
    ["getAllAreas", formData.stateId],
    () => getAllAreas({
      searchId: formData.stateId,
      searchType: "area"
    }),
    {
      enabled: !!formData.stateId,
    }
  );

  useEffect(() => {
    if (allAreasData?.data && !allAreasIsError && !allAreasLoading) {
      const transformedData = allAreasData?.data?.map((area: any) => ({
        value: area.id,
        label: area.name
      }));
      setAreasData(transformedData);
    }
  }, [allAreasData, allAreasIsError, allAreasLoading]);

  const {
    data: allSuburbsData,
    isLoading: allSuburbsLoading,
    isError: allSuburbsIsError,
  } = useGetRequestQuery(
    ["getAllSuburbs", formData.areaId],
    () => getAllAreas({
      searchId: formData.areaId,
      searchType: "suburb"
    }),
    {
      enabled: !!formData.areaId,
    }
  );

  useEffect(() => {
    if (allSuburbsData?.data && !allSuburbsIsError && !allSuburbsLoading) {
      const transformedSuburbsData = allSuburbsData?.data?.map((suburb: any) => ({
        value: suburb.id,
        label: suburb.name,
      }));
      setSuburb(transformedSuburbsData);
    }
  }, [allSuburbsData, allSuburbsIsError, allSuburbsLoading]);

  useEffect(() => {
    if (builderData?.data && !isLoading) {
      const { stateId, areaId, updateOn, createdOn, isDeleted, statecheckbox, ...filteredData } = builderData.data;
      
      // Process statecheckbox - ensure it's an array of strings
      let processedStateCheckbox: string[] = [];
      if (Array.isArray(statecheckbox)) {
        processedStateCheckbox = statecheckbox.map(String);
      } else if (typeof statecheckbox === 'string' && statecheckbox) {
        processedStateCheckbox = statecheckbox.split(',').map(s => s.trim()).filter(Boolean);
      }

      // Saved method hi rakho; agar match na ho to blank taaki "Select" placeholder dikhe
      const isValidMethod = methodOptions.some(opt => opt.value === filteredData.method);
      const resolvedMethod = isValidMethod ? filteredData.method : "";

      setFormData({ 
        ...formData, 
        ...filteredData,
        method: resolvedMethod,
        stateId: stateId?.toString(), // Convert to string to match dropdown value format
        areaId: areaId?.toString(), // Convert to string to match dropdown value format
        statecheckbox: processedStateCheckbox
      });
    }
  }, [builderData, isLoading]);

  const { mutate: updateBuilder, isPending: isUpdating } = useMutationRequestQuery(
    "updateBuilder",
    updateBuilderService,
    {
      onSuccess: () => {
        toast.success("Builder updated successfully.");
        queryClient.invalidateQueries({ queryKey: ["getAllBuilders"] });
        queryClient.invalidateQueries({ queryKey: ["getBuilderById", builderID] });
        queryClient.invalidateQueries({ queryKey: ["getSupplierComments", builderID] });
        // Clear the comment field
        setFormData((prev: typeof formData) => ({
          ...prev,
          newComment: ""
        }));
      },
      onError: () => {
        toast.error("Failed to update builder.");
      },
    }
  );

  const { mutate: addComment } = useMutationRequestQuery(
    "addSupplierComment",
    getSupplierCommentsService,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["getSupplierComments", builderID] });
      }
    }
  );

  // All fields mandatory: Company Name, Initial, Method, Followup,
  // BDM First/Last/Mobile/Email, Accounts First/Last/Mobile/Email,
  // State selection, Comments
  const validateForm = () => {
    const errors: any = {};

    if (!formData.supplier || !formData.supplier.toString().trim()) {
      errors.supplier = "Company Name is required.";
    }

    if (!formData.method || formData.method === "Select") {
      errors.method = "Method is required.";
    }

    if (!formData.followUp) {
      errors.followUp = "Followup date is required.";
    }

    if (!formData.bdmMobile || !formData.bdmMobile.toString().trim()) {
      errors.bdmMobile = "BDM Mobile number is required.";
    }

    if (!formData.bdmEmail || !formData.bdmEmail.toString().trim()) {
      errors.bdmEmail = "BDM Email is required.";
    } else if (!isValidEmail(formData.bdmEmail)) {
      errors.bdmEmail = "Enter a valid email address.";
    }

    if (!formData.accMobile || !formData.accMobile.toString().trim()) {
      errors.accMobile = "Accounts Mobile number is required.";
    }

    if (!formData.accEmail || !formData.accEmail.toString().trim()) {
      errors.accEmail = "Accounts Email is required.";
    } else if (!isValidEmail(formData.accEmail)) {
      errors.accEmail = "Enter a valid email address.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error("Please fill all mandatory fields.");
      return;
    }

    // Create a copy of formData to modify
    const submitData = { ...formData };

    // Prepare payload for backend: convert statecheckbox to comma-separated string for submission only
    const payload = {
      ...submitData,
      statecheckbox: Array.isArray(submitData.statecheckbox)
        ? submitData.statecheckbox.join(',')
        : '',
    } as typeof submitData & { statecheckbox: string };

    // Handle empty comment
    if (!payload.newComment || payload.newComment.trim() === "") {
      payload.newComment = " ";
    }

    // Handle unselected location fields
    if (!payload.stateId) {
      payload.stateId = 0;
    }
    if (!payload.areaId) {
      payload.areaId = 0;
    }
    if (!payload.suburbId) {
      payload.suburbId = 0;
    }

    // Update the builder with the data
    updateBuilder({ builderID, data: payload });
  };

  const handleCheckboxChange = (stateId: number) => {
    setFormData((prev: FormData) => {
      const currentStateCheckbox = Array.isArray(prev.statecheckbox) ? prev.statecheckbox : [];
      const stateIdStr = stateId.toString();
      const updatedCheckbox = currentStateCheckbox.includes(stateIdStr)
        ? currentStateCheckbox.filter(id => id !== stateIdStr)
        : [...currentStateCheckbox, stateIdStr];
      return { ...prev, statecheckbox: updatedCheckbox };
    });
    setFormErrors((prevErrors: any) => ({
      ...prevErrors,
      statecheckbox: "",
    }));
  };

  const handleFileChange = (e: any, fieldName: string) => {
    const file = e.target.files[0];

    if (file) {
      setFormData((prevData: any) => ({
        ...prevData,
        [fieldName]: file, // Store file directly
      }));
    }
  };

  const { data: commentsData } = useGetRequestQuery(
    ["getSupplierComments", builderID],
    () => getSupplierCommentsService(builderID),
    { enabled: !!builderID }
  );

  useEffect(() => {
    if (commentsData?.data) {
      setAllComments(commentsData.data);
    }
  }, [commentsData]);

  return (
    <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'}>
      <div className="table-responsive h-100 py-4">
        <section className='bg-white'>

          <div className="scrollable-table-container">
            <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
              <div className="d-flex align-items-center">
                <Col xs={12} className='d-flex'>
                  <H3 className="heading-color f-w-900 text-left my-2">Real Estate Builder</H3>
                </Col>
              </div>
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Company Name: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '25%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    setValue={handleValueChange}
                    placeholder="Company Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={30}
                  />
                  {formErrors.supplier && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.supplier}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Initial : </Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="initial"
                    value={formData.initial}
                    setValue={handleValueChange}
                    placeholder="Company Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.initial && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.initial}</div>
                  )}
                </Col>
                {/* <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Type:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="builderType"
                    value={formData.builderType}
                    setValue={handleValueChange}
                    options={[ {value: "Builder", label: "Builder"}, {value: "Real Estate Builder", label: "Real Estate Builder"}]}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              {/* PPT annotation: "New Column - Method" (dropdown list) + "New Column - Followup" (calendar date), added directly below Company Name / Initial row, same layout style */}
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Method: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '25%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="method"
                    value={formData.method}
                    setValue={handleValueChange}
                    options={methodOptions}
                    passCol="col-9"
                    design="flex"
                    placeholder="Select"
                  />
                  {formErrors.method && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.method}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '10%', paddingInline: '2px' }}>
                  <Label className="mb-3">Followup: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <SelectDatePicker
                    label=""
                    name="followUp"
                    value={formData.followUp}
                    setValue={handleValueChange}
                    design="flex"
                    passCol="col-9"
                  />
                  {formErrors.followUp && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.followUp}</div>
                  )}
                </Col>
              </div>
              <div style={{ borderBottom: '3px solid #0000ff' }} className="my-3"></div>
              <div className='d-flex item-center mt-2'>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Role</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">First Name</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Surname</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Mobile</H3>
                </Col>
                <Col className="responsive-col heading-color" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <H3 className="mb-3 f-w-900">Email</H3>
                </Col>
              </div>
              {/* <div className='d-flex item-center '>
                <Col className="responsive-col " xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Stock List:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    mobileField="mobile"
                    type="text"
                    value={formData.mobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="email"
                    value={formData.email}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col>
              </div> */}
              <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">BDM: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="bdmFirstName"
                    value={formData.bdmFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.bdmFirstName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmFirstName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="bdmLastName"
                    value={formData.bdmLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.bdmLastName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmLastName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="bdmMobile"
                    value={formData.bdmMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                  {formErrors.bdmMobile && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmMobile}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="bdmEmail"
                    value={formData.bdmEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                  {formErrors.bdmEmail && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.bdmEmail}</div>
                  )}
                </Col>
              </div>
              <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Accounts: <span style={{ color: 'red' }}>*</span></Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="accFirstName"
                    value={formData.accFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.accFirstName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accFirstName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="accLastName"
                    value={formData.accLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                  {formErrors.accLastName && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accLastName}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="accMobile"
                    value={formData.accMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                  {formErrors.accMobile && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accMobile}</div>
                  )}
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '30%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="accEmail"
                    value={formData.accEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                  {formErrors.accEmail && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.accEmail}</div>
                  )}
                </Col>
              </div>
              {/* <div className='d-flex item-center '>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Construction:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="consFirstName"
                    value={formData.consFirstName}
                    setValue={handleValueChange}
                    placeholder="First Name"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="consLastName"
                    value={formData.consLastName}
                    setValue={handleValueChange}
                    placeholder="Surname"
                    design="flex"
                    passCol="col-9"
                    maxChars={20}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <MobileField
                    label=""
                    type="text"
                    mobileField="consMobile"
                    value={formData.consMobile}
                    setValue={handleValueChange}
                    placeholder="Mobile"
                    design="flex"
                    passCol="col-9"
                    maxLength={14}
                  />
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="email"
                    name="consEmail"
                    value={formData.consEmail}
                    setValue={handleValueChange}
                    placeholder="Email"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col>
              </div> */}
              <div style={{ borderBottom: '3px solid #0000ff' }} className="my-3"></div>
              <div className="d-flex align-items-center">
                {/* <Col xs={3} className='d-flex' style={{ width: '31%', paddingInline: '2px' }}>
                  <H3 className="heading-color f-w-900 text-left my-2">Stock Portal</H3>
                </Col> */}
                <Col xs={6} className='d-flex'>
                  <H3 className="heading-color f-w-900 text-left my-2">Locations</H3>
                </Col>
              </div>


               <div className="state-checkbox-group mt-2 d-flex align-items-center">
                <Label className="mb-2">State: </Label>
                <div className="checkboxes-container " style={{ display: 'flex', alignItems: "center", gap: '20px', marginBottom:'6px', marginLeft: '8%' }}>
                  {statesData.map((opt: { value: number; label: string }) => (
                    <label key={opt.value} className="circle-checkbox">
                      <input
                        style={{ width: '10px', marginLeft: "10px", height: '10px', borderRadius: 50, border: '2px solid #000' }}
                        type="checkbox"
                        checked={Array.isArray(formData.statecheckbox) && formData.statecheckbox.includes(opt.value.toString())}
                        onChange={() => handleCheckboxChange(opt.value)}
                      />
                      <span className="custom-circle" />
                      <span className="label-text" style={{ marginLeft: '5px' }}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {formErrors.statecheckbox && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.statecheckbox}</div>
              )}



              <div className='d-flex item-center mt-2'>
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Username:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="text"
                    name="username"
                    value={formData.username}
                    setValue={handleValueChange}
                    placeholder="Enter Username"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col> */}
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Area:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="areaId"
                    value={formData.areaId}
                    setValue={handleValueChange}
                    options={areasData}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              <div className='d-flex item-center mt-2'>
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Password:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <TextField
                    label=""
                    type="password"
                    name="password"
                    value={formData.password}
                    setValue={handleValueChange}
                    placeholder="Enter Password"
                    design="flex"
                    passCol="col-9"
                    maxChars={50}
                  />
                </Col> */}
                {/* <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Suburb:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DropdownWithValueName
                    label=""
                    name="suburbId"
                    value={formData.suburbId}
                    setValue={handleValueChange}
                    options={suburb}
                    passCol="col-9"
                    design="flex"
                  />
                </Col> */}
              </div>
              {/* <div className='d-flex item-center mt-2'>
                <Col className="responsive-col" xs={12} style={{ width: '11%', paddingInline: '2px' }}>
                  <Label className="mb-3">Updated:</Label>
                </Col>
                <Col className="responsive-col" xs={12} style={{ width: '20%', paddingInline: '2px' }}>
                  <DatePicker
                    label=""
                    name="updateOn"
                    value={formData.updateOn}
                    setValue={handleValueChange}
                    design="flex"
                    passCol="col-9"
                  />
                </Col>
              </div> */}


            {/* Add Comments Section */}
            <div className='mt-2' style={{marginBottom: '20px', marginLeft: '1%'}}>
              <Col xs={12} style={{ width: '100%', paddingInline: '2px' }}>
                <H3 className="heading-color f-w-900 text-left my-2">Comments </H3>
                <Input
                  type="textarea"
                  name="newComment"
                  value={formData.newComment}
                  onChange={(e) => handleValueChange("newComment", e.target.value)}
                  placeholder="Enter Comments"
                  className="w-75"
                  style={{ height: '100px' }}
                />
                {formErrors.newComment && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>{formErrors.newComment}</div>
                )}
              </Col>
            </div>

            {/* Display Comments */}
            {allComments.length > 0 && (
              <div style={{marginLeft: '1%', width: '74%' }}>
                <ListGroup className="mt-3">
                  {allComments.map((comment: any, index: any) => (
                    <ListGroupItem key={index} className="flex-column">
                      {/* <div className="d-flex justify-content-between">
                        <span>
                          <strong>Date:</strong> {new Date(comment.createdOn).toLocaleString("en-GB", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          }).replace(",", "")}
                        </span>
                        <span>
                          <strong>Staff Name:</strong> {comment.staff || "N/A"}
                        </span>
                      </div> */}
                      <div className="mt-2" style={{fontWeight:'bold'}}>{comment.commentText}</div>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </div>
            )}







              {/* <div style={{ borderBottom: '3px solid #0000ff' }} className="my-3"></div> */}
              {/* <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Build Facade</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "PropertyImageFile")}
                  />
                  {(formData.PropertyImageFile || formData.propertyImage) && (
                    <img
                      src={
                        formData.PropertyImageFile
                          ? URL.createObjectURL(formData.PropertyImageFile) // New file preview
                          : `${BASE_URL}${formData.propertyImage}` // API image
                      }
                      alt="Property"
                      style={{
                        width: "200px",
                        height: "200px",
                        marginTop: "10px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label>Stock Report</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "BuildFacadeFile")}
                  />
                  {(formData.BuildFacadeFile || formData.buildfacade) && (
                    <img
                      src={
                        formData.BuildFacadeFile
                          ? URL.createObjectURL(formData.BuildFacadeFile) // New file preview
                          : `${BASE_URL}${formData.buildfacade}` // API image
                      }
                      alt="Facade"
                      style={{
                        width: "200px",
                        height: "200px",
                        marginTop: "10px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  )}
                </div>
              </div> */}
            </table>

            <div className="d-flex gap-3 mt-3 mb-3">
              <Button onClick={() => navigate("/CRMBuilders")} color="secondary">
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                disabled={isUpdating}
                type="submit"
              >
                {isUpdating ? "Loading . . ." : "Update"}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}